import React, { useState, useEffect } from "react";
import { useBusinessPlan } from "../../App";
import { useCurrency } from "../../contexts/CurrencyContext";
import axios from "axios";
import Cookies from "js-cookie";
import { Tooltip as InfoTooltip } from "../ui/Tooltip";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Package,
  AlertCircle,
  CheckCircle,
  XCircle,
  Calendar,
  Layers
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { 
  BarChart, 
  Bar,
  PieChart,
  Pie,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  Cell
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

const RevenueAnalysis = () => {
  const { currentPlan } = useBusinessPlan();
  const { formatCurrency: formatCurrencyContext } = useCurrency();
  const [activeTab, setActiveTab] = useState("product");
  
  // Data states
  const [productData, setProductData] = useState(null);
  const [streamData, setStreamData] = useState(null);
  const [periodData, setPeriodData] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (currentPlan?.id) {
      fetchAllData();
    }
  }, [currentPlan]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = Cookies.get("enakid_token");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      };
      
      const [productRes, streamRes, periodRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/actual-data/revenue-analysis/${currentPlan.id}`, { headers }),
        axios.get(`${BACKEND_URL}/api/actual-data/revenue-by-stream/${currentPlan.id}`, { headers }),
        axios.get(`${BACKEND_URL}/api/actual-data/revenue-by-period/${currentPlan.id}`, { headers })
      ]);
      
      setProductData(productRes.data);
      setStreamData(streamRes.data);
      setPeriodData(periodRes.data);
    } catch (err) {
      console.error("Error fetching revenue analysis:", err);
      setError(err.response?.data?.detail || "Failed to load revenue analysis");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return formatCurrencyContext(value || 0, currentPlan?.currency);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      exceeding: { color: "bg-blue-100 text-blue-800", icon: TrendingUp, label: "Exceeding" },
      on_track: { color: "bg-green-100 text-green-800", icon: CheckCircle, label: "On Track" },
      below_target: { color: "bg-red-100 text-red-800", icon: TrendingDown, label: "Below Target" }
    };
    
    const config = statusConfig[status] || statusConfig.on_track;
    const Icon = config.icon;
    
    return (
      <Badge className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-slate-600">Loading Revenue Analysis...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
        <div className="max-w-7xl mx-auto">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 text-red-600">
                <AlertCircle className="w-6 h-6" />
                <p className="font-medium">{error}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!productData?.product_analysis?.length) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
        <div className="max-w-7xl mx-auto">
          <Card>
            <CardContent className="p-12 text-center">
              <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Revenue Data Available</h3>
              <p className="text-gray-600 mb-6">
                Start recording sales actuals to see detailed revenue analysis.
              </p>
              <button
                onClick={() => window.location.href = "/sales-actuals-entry"}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Record Sales Actuals
              </button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <InfoTooltip content="Revenue Analysis - Deep-dive into revenue performance by Product, Stream, and Time Period. Use to: Identify best/worst performers, Assess revenue model mix, Spot trends and seasonality. Drill down from Executive Dashboard's summary to product-level insights.">
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <DollarSign className="w-8 h-8 text-green-600" />
                Revenue Analysis
              </h1>
            </InfoTooltip>
            <p className="text-gray-600 mt-2">
              Comprehensive revenue performance breakdown and insights
            </p>
          </div>
          <InfoTooltip content="Refresh data with latest sales actuals. Click after entering new actuals or before important meetings. Takes 2-5 seconds.">
            <button
              onClick={fetchAllData}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </InfoTooltip>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="pb-3">
              <InfoTooltip content="Sum of ALL product revenue targets from Strategic Framework for selected period. Your revenue goal.">
                <CardTitle className="text-sm font-medium text-gray-600">Total Planned</CardTitle>
              </InfoTooltip>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(productData.summary.total_planned)}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <InfoTooltip content="Sum of ALL actual revenue recorded in Sales Actuals for selected period. This is what you really achieved.">
                <CardTitle className="text-sm font-medium text-gray-600">Total Actual</CardTitle>
              </InfoTooltip>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(productData.summary.total_actual)}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <InfoTooltip content="Revenue performance scorecard. Formula: (Actual - Planned) / Planned × 100. Positive (green) = Exceeded targets. Negative (red) = Missed targets. >±10% = material variance requiring action.">
                <CardTitle className="text-sm font-medium text-gray-600">Variance</CardTitle>
              </InfoTooltip>
            </CardHeader>
            <CardContent>
              <p className={`text-2xl font-bold ${productData.summary.total_variance_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {productData.summary.total_variance_pct > 0 ? "+" : ""}{productData.summary.total_variance_pct.toFixed(1)}%
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {formatCurrency(productData.summary.total_variance_amount)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <InfoTooltip content="Number of products/services with recorded sales actuals. Shows portfolio size and data completeness. Low count may indicate missing data entry.">
                <CardTitle className="text-sm font-medium text-gray-600">Products</CardTitle>
              </InfoTooltip>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-gray-900">{productData.summary.product_count}</p>
              <p className="text-sm text-gray-600 mt-1">Tracked</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <InfoTooltip content="Product-level revenue breakdown. Shows: Bar chart (plan vs actual per SKU), Waterfall (variance contribution), Table (detailed metrics). Use to: Identify best/worst products, Make portfolio decisions, Review product manager performance.">
              <TabsTrigger value="product">By Product/Service</TabsTrigger>
            </InfoTooltip>
            <InfoTooltip content="Revenue model analysis. Shows: Pie chart (% by stream type), Bar chart (plan vs actual by stream), Table (stream performance). Types: Subscriptions (recurring), One-time (transactional), Licensing, Services. Use to: Assess model diversification, Track recurring revenue %, Present to investors.">
              <TabsTrigger value="stream">By Revenue Stream</TabsTrigger>
            </InfoTooltip>
            <InfoTooltip content="Trend analysis over time. Shows: Line chart (monthly trends), Area chart (cumulative YTD), Table (quarterly summary). Use to: Spot seasonality, Calculate growth rates (MoM/QoQ/YoY), Forecast annual performance, Identify momentum shifts.">
              <TabsTrigger value="period">By Time Period</TabsTrigger>
            </InfoTooltip>
          </TabsList>

          {/* Tab 1: By Product/Service */}
          <TabsContent value="product" className="space-y-6">
            {/* Bar Chart */}
            <Card>
              <CardHeader>
                <InfoTooltip content="Side-by-side plan vs actual revenue per product. Taller bars = Higher revenue. Gaps = Variance.">
                  <CardTitle>Revenue Comparison by SKU</CardTitle>
                </InfoTooltip>
                <CardDescription>Planned vs Actual revenue for each product</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={productData.product_analysis}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="product_sku" angle={-45} textAnchor="end" height={80} />
                    <YAxis tickFormatter={(value) => formatCurrency(value)} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Bar dataKey="planned_revenue" fill="#93c5fd" name="Planned" />
                    <Bar dataKey="actual_revenue" fill="#10b981" name="Actual" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Waterfall Chart */}
            <Card>
              <CardHeader>
                <InfoTooltip content="Variance waterfall from planned to actual. Green bars = Products exceeding plan. Red bars = Products below plan. Shows cumulative contribution.">
                  <CardTitle>Revenue Waterfall (Plan → Actual)</CardTitle>
                </InfoTooltip>
                <CardDescription>Variance contribution by product</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={productData.waterfall_data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" angle={-45} textAnchor="end" height={100} />
                    <YAxis tickFormatter={(value) => formatCurrency(value)} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="value" name="Revenue">
                      {productData.waterfall_data.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.type === 'total' ? '#3b82f6' : entry.type === 'positive' ? '#10b981' : '#ef4444'} 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Product Table */}
            <Card>
              <CardHeader>
                <CardTitle>Product Performance Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3 font-semibold">Product</th>
                        <th className="text-left p-3 font-semibold">SKU</th>
                        <th className="text-right p-3 font-semibold">Plan</th>
                        <th className="text-right p-3 font-semibold">Actual</th>
                        <th className="text-right p-3 font-semibold">Variance</th>
                        <th className="text-right p-3 font-semibold">%</th>
                        <th className="text-right p-3 font-semibold">YTD</th>
                        <th className="text-center p-3 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productData.product_analysis.map((product, index) => (
                        <tr key={index} className="border-b hover:bg-gray-50">
                          <td className="p-3 font-medium">{product.product_name}</td>
                          <td className="p-3">{product.product_sku}</td>
                          <td className="p-3 text-right">{formatCurrency(product.planned_revenue)}</td>
                          <td className="p-3 text-right font-medium">{formatCurrency(product.actual_revenue)}</td>
                          <td className="p-3 text-right">
                            <span className={product.variance_amount >= 0 ? 'text-green-600' : 'text-red-600'}>
                              {formatCurrency(product.variance_amount)}
                            </span>
                          </td>
                          <td className="p-3 text-right">
                            <span className={`font-bold ${product.variance_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {product.variance_pct > 0 ? "+" : ""}{product.variance_pct.toFixed(1)}%
                            </span>
                          </td>
                          <td className="p-3 text-right">{formatCurrency(product.ytd_revenue)}</td>
                          <td className="p-3 text-center">{getStatusBadge(product.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 2: By Revenue Stream */}
          <TabsContent value="stream" className="space-y-6">
            {streamData && (
              <>
                {/* Pie Chart */}
                <Card>
                  <CardHeader>
                    <InfoTooltip content="Revenue % by stream type. Larger slices = Bigger contributors. Use to assess diversification and recurring revenue %.">
                      <CardTitle>Revenue Contribution by Stream</CardTitle>
                    </InfoTooltip>
                    <CardDescription>Percentage breakdown of actual revenue</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <Pie
                          data={streamData.stream_analysis}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          label={(entry) => `${entry.stream_name}: ${entry.contribution_pct}%`}
                          outerRadius={120}
                          fill="#8884d8"
                          dataKey="actual"
                        >
                          {streamData.stream_analysis.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Stacked Bar */}
                <Card>
                  <CardHeader>
                    <InfoTooltip content="Plan vs actual per revenue stream. Identify which stream types are meeting, exceeding, or missing targets.">
                      <CardTitle>Plan vs Actual by Stream</CardTitle>
                    </InfoTooltip>
                    <CardDescription>Comparison across revenue streams</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={streamData.stream_analysis}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="stream_name" angle={-45} textAnchor="end" height={100} />
                        <YAxis tickFormatter={(value) => formatCurrency(value)} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                        <Bar dataKey="planned" fill="#93c5fd" name="Planned" />
                        <Bar dataKey="actual" fill="#10b981" name="Actual" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Stream Details */}
                <Card>
                  <CardHeader>
                    <CardTitle>Revenue Stream Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {streamData.stream_analysis.map((stream, idx) => (
                        <div key={idx} className="border rounded-lg p-4">
                          <div className="flex justify-between items-center mb-2">
                            <h4 className="font-bold text-lg">{stream.stream_name}</h4>
                            <span className="text-sm font-medium text-gray-600">
                              {stream.contribution_pct}% of total
                            </span>
                          </div>
                          <div className="grid grid-cols-3 gap-4 mb-3">
                            <div>
                              <p className="text-xs text-gray-600">Planned</p>
                              <p className="font-semibold">{formatCurrency(stream.planned)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600">Actual</p>
                              <p className="font-semibold">{formatCurrency(stream.actual)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600">Variance</p>
                              <p className={`font-semibold ${stream.variance_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {stream.variance_pct > 0 ? "+" : ""}{stream.variance_pct.toFixed(1)}%
                              </p>
                            </div>
                          </div>
                          {stream.products && (
                            <div className="border-t pt-2">
                              <p className="text-xs font-medium text-gray-700 mb-2">Linked Products:</p>
                              <div className="space-y-1">
                                {stream.products.map((product, pIdx) => (
                                  <div key={pIdx} className="flex justify-between text-sm bg-gray-50 p-2 rounded">
                                    <span>{product.product_name} ({product.product_sku})</span>
                                    <span className="font-medium">{formatCurrency(product.actual)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Tab 3: By Time Period */}
          <TabsContent value="period" className="space-y-6">
            {periodData && (
              <>
                {/* Line Chart */}
                <Card>
                  <CardHeader>
                    <InfoTooltip content="Month-by-month trends. Blue line = Planned. Green line = Actual. Spot seasonality and consistency patterns.">
                      <CardTitle>Monthly Revenue Trend</CardTitle>
                    </InfoTooltip>
                    <CardDescription>Plan vs Actual over time</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={350}>
                      <LineChart data={periodData.monthly_trend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis tickFormatter={(value) => formatCurrency(value)} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                        <Line type="monotone" dataKey="planned" stroke="#3b82f6" strokeWidth={2} name="Planned" />
                        <Line type="monotone" dataKey="actual" stroke="#10b981" strokeWidth={2} name="Actual" />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Area Chart */}
                <Card>
                  <CardHeader>
                    <InfoTooltip content="YTD cumulative revenue accumulation. Shows if you're catching up to plan or falling further behind. Use to forecast annual performance.">
                      <CardTitle>Cumulative Revenue (YTD)</CardTitle>
                    </InfoTooltip>
                    <CardDescription>Year-to-date accumulation</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={350}>
                      <AreaChart data={periodData.monthly_trend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis tickFormatter={(value) => formatCurrency(value)} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                        <Area type="monotone" dataKey="cumulative_planned" stackId="1" stroke="#3b82f6" fill="#93c5fd" name="Cumulative Planned" />
                        <Area type="monotone" dataKey="cumulative_actual" stackId="2" stroke="#10b981" fill="#6ee7b7" name="Cumulative Actual" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Period Table */}
                <Card>
                  <CardHeader>
                    <InfoTooltip content="Quarterly summary table: Planned, Actual, Variance ($), and Variance (%) per quarter.">
                      <CardTitle>Quarterly Breakdown</CardTitle>
                    </InfoTooltip>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left p-3 font-semibold">Quarter</th>
                            <th className="text-right p-3 font-semibold">Planned</th>
                            <th className="text-right p-3 font-semibold">Actual</th>
                            <th className="text-right p-3 font-semibold">Variance</th>
                            <th className="text-right p-3 font-semibold">%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {periodData.quarterly_summary.map((quarter, index) => {
                            const variance = calculate_variance(quarter.actual, quarter.planned);
                            return (
                              <tr key={index} className="border-b hover:bg-gray-50">
                                <td className="p-3 font-medium">{quarter.quarter}</td>
                                <td className="p-3 text-right">{formatCurrency(quarter.planned)}</td>
                                <td className="p-3 text-right font-medium">{formatCurrency(quarter.actual)}</td>
                                <td className="p-3 text-right">
                                  <span className={quarter.actual - quarter.planned >= 0 ? 'text-green-600' : 'text-red-600'}>
                                    {formatCurrency(quarter.actual - quarter.planned)}
                                  </span>
                                </td>
                                <td className="p-3 text-right">
                                  <span className={`font-bold ${variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {variance > 0 ? "+" : ""}{variance.toFixed(1)}%
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Helper function
const calculate_variance = (actual, planned) => {
  if (planned === 0) return actual === 0 ? 0 : 100;
  return ((actual - planned) / planned) * 100;
};

export default RevenueAnalysis;
